import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Button,
  DatePicker,
  Input,
  Select,
  TimePicker,
  Row,
  Col,
  Tooltip,
} from "antd";
import moment from "moment";
import {
  stayData,
  typeOfEventOptions,
  fuelTypeOptions,
  lubOilTypeOptions,
  bunkerGradeOptions,
} from "../../../Data/data";
import { ApiURL } from "../../../services/apiConstants";
import { post } from "../../../services/services";
import { useLocation } from "react-router-dom";
import {
  ErrorToast,
  SuccessToast,
  WarningToast,
} from "../../../widgets/AppToast";
import { useWatch } from "antd/es/form/Form";
import {
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";

const DailyConsumption = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const [staffDashboard, setStaffDashboard] = useState(null);
  const [loader, setLoader] = useState(false);
  const [robFuel, setRobFuel] = useState([]);
  const [previousLog, setPreviousLog] = useState(null);
  const addOtherConsumptionRef = useRef(null);
  const addOtherlubeConsumptionRef = useRef(null);
  const addOtherAuxiConsumptionRef = useRef(null);
  const addOtherBoilerConsumptionRef = useRef(null);
  const typeOfEvent = useWatch("type_of_event", form);
  const [calculations, setCalculations] = useState({
    total_duration: 0,
    anchor: 0,
    ss: 0,
    drift: 0,
    hm: 0,
    port: 0,
    total_revolution: null,
    rpm: null,
    engine_distance: null,
    speed: null,
    slip: null,
  });

  useEffect(() => {
    if (typeOfEvent === "NOON") {
      form.setFieldsValue({
        time: moment("12:00:00", "HH:mm:ss"),
      });
    } else {
      form.setFieldsValue({
        time: null,
      });
    }
  }, [typeOfEvent, form]);

  // Initialize staff dashboard and fetch previous log
  useEffect(() => {
    if (location.pathname === "/add-daily-consumption") {
      const staffDashboards = localStorage.getItem("staff_dashboard");
      let parsedData = null;
      try {
        parsedData = JSON.parse(staffDashboards);
      } catch {
        parsedData = null;
      }
      setStaffDashboard(parsedData);
      if (parsedData && parsedData.voyage_details?.[0]?.id) {
        form.setFieldsValue({
          voyage_number: parsedData.voyage_details[0].voyage_no,
        });
        fetchPreviousLog(parsedData.voyage_details[0].id);
      }
    }
    // eslint-disable-next-line
  }, [location.pathname, form]);

  // Fetch previous log
  const fetchPreviousLog = async (voyageId) => {
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("voyage_id", voyageId);
      const res = await post(
        ApiURL.get_voyage_and_consumption_summary,
        FORM_DATA
      );
      if (res?.status === true) {
        setPreviousLog({
          log_date: res?.response_data?.log_date,
          log_time: res?.response_data?.log_time,
          me_revolution_counter:
            parseFloat(res?.response_data?.me_revolution_counter) || 0,
          pitch: res?.response_data?.pitch || 0,
        });
        setRobFuel(res?.response_data?.rob_fuel || []);
      } else {
        setPreviousLog({
          log_date: moment().format("YYYY-MM-DD"),
          log_time: "00:00:00",
          me_revolution_counter: 0,
        });
      }
    } catch (error) {
      setPreviousLog({
        log_date: moment().format("YYYY-MM-DD"),
        log_time: "00:00:00",
        me_revolution_counter: 0,
      });
    }
  };

  // Real-time calculations based on Excel formulas
  const calculateValues = (values) => {
    const newCalculations = {
      total_duration: 0,
      anchor: 0,
      ss: 0,
      drift: 0,
      hm: 0,
      port: 0,
      total_revolution: null,
      rpm: null,
      engine_distance: null,
      speed: null,
      slip: null,
    };

    let updatedRobFuel = robFuel.map((item) => ({ ...item }));

    // 1. Bunker received
    if (
      values.bunker_received &&
      values.bunker_quantity &&
      values.bunker_grade
    ) {
      const quantity = parseFloat(values.bunker_quantity);
      if (!isNaN(quantity)) {
        const existingFuel = updatedRobFuel.find(
          (item) => item.fuelType === values.bunker_grade
        );
        if (existingFuel) {
          existingFuel.Initialiy_rob = (
            parseFloat(existingFuel.Initialiy_rob || 0) + quantity
          ).toString();
        } else {
          updatedRobFuel.push({
            fuelType: values.bunker_grade,
            Initialiy_rob: quantity.toString(),
          });
        }
      }
    }

    // 2. Fuel/Water consumption
    const consumptionFields = [
      { field: "main_engine_fuel_consumption", type: "main_fuel_oil_type" },
      {
        field: "auxiliary_engine_fuel_consumption",
        type: "auxi_fuel_oil_type",
        extra: "aux_engine_fuel_consumption_cargo",
      },
      {
        field: "boiler_fuel_consumption",
        type: "boiler_fuel_oil_type",
        extra: "boiler_fuel_consumption_cargo",
      },
      { field: "cargo_fuel_consumption", type: "cargo_fuel_oil_type" },
      { field: "cylinder_oil_consumption", type: "lub_oil_type" },
      { field: "fresh_water_consumption", type: () => "FW" },
      { field: "fw_production", type: () => "FW" },
    ];

    consumptionFields.forEach(({ field, type, extra }) => {
      const consumption = parseFloat(values[field] || 0);
      if (consumption > 0) {
        let totalConsumption = consumption;
        if (extra && values[extra]) {
          const extraConsumption = parseFloat(values[extra]);
          if (!isNaN(extraConsumption)) {
            totalConsumption += extraConsumption;
          }
        }

        const fuelType = typeof type === "function" ? type() : values[type];
        if (fuelType) {
          const existingFuel = updatedRobFuel.find(
            (item) => item.fuelType === fuelType
          );
          if (existingFuel) {
            const initialRob = parseFloat(existingFuel.Initialiy_rob || 0);
            const newRob = Math.max(0, initialRob - totalConsumption);
            existingFuel.Initialiy_rob = newRob.toString();
          } else {
            updatedRobFuel.push({
              fuelType,
              Initialiy_rob: (0 - totalConsumption).toString(),
            });
          }
        }
      }
    });

    // Me Other consumptions
    if (Array.isArray(values.me_other_consumption)) {
      values.me_other_consumption.forEach((entry) => {
        if (!entry) return;
        const { fuel_type, fuel_consumption } = entry;
        const consumption = parseFloat(fuel_consumption || 0);
        if (consumption > 0 && fuel_type) {
          const existingFuel = updatedRobFuel.find(
            (item) => item.fuelType === fuel_type
          );
          if (existingFuel) {
            const initialRob = parseFloat(existingFuel.Initialiy_rob || 0);
            const newRob = Math.max(0, initialRob - consumption);
            existingFuel.Initialiy_rob = newRob.toString();
          } else {
            updatedRobFuel.push({
              fuelType: fuel_type,
              Initialiy_rob: (0 - consumption).toString(),
            });
          }
        }
      });
    }

    // Lub Oil Other consumptions - ROB update
    if (Array.isArray(values.lub_other_consumption)) {
      values.lub_other_consumption.forEach((entry) => {
        if (!entry) return;

        const { lub_fuel_type, lub_fuel_consumption } = entry;
        const consumption = parseFloat(lub_fuel_consumption || 0);

        if (consumption > 0 && lub_fuel_type) {
          const existingFuel = updatedRobFuel.find(
            (item) => item.fuelType === lub_fuel_type
          );

          if (existingFuel) {
            const initialRob = parseFloat(existingFuel.Initialiy_rob || 0);
            const newRob = Math.max(0, initialRob - consumption);
            existingFuel.Initialiy_rob = newRob.toString();
          } else {
            updatedRobFuel.push({
              fuelType: lub_fuel_type,
              Initialiy_rob: (0 - consumption).toString(),
            });
          }
        }
      });
    }

    // Auxiliary Other consumptions
    if (Array.isArray(values.auxi_other_consumption)) {
      values.auxi_other_consumption.forEach((entry) => {
        if (!entry) return;
        const { auxi_fuel_type, auxi_fuel_consumption } = entry;
        const consumption = parseFloat(auxi_fuel_consumption || 0);
        if (consumption > 0 && auxi_fuel_type) {
          const existingFuel = updatedRobFuel.find(
            (item) => item.fuelType === auxi_fuel_type
          );
          if (existingFuel) {
            const initialRob = parseFloat(existingFuel.Initialiy_rob || 0);
            const newRob = Math.max(0, initialRob - consumption);
            existingFuel.Initialiy_rob = newRob.toString();
          } else {
            updatedRobFuel.push({
              fuelType: auxi_fuel_type,
              Initialiy_rob: (0 - consumption).toString(),
            });
          }
        }
      });
    }

    // Boiler Other consumptions
    if (Array.isArray(values.boiler_other_consumption)) {
      values.boiler_other_consumption.forEach((entry) => {
        if (!entry) return;
        const { boiler_fuel_type, boiler_fuel_consumption } = entry;
        const consumption = parseFloat(boiler_fuel_consumption || 0);
        if (consumption > 0 && boiler_fuel_type) {
          const existingFuel = updatedRobFuel.find(
            (item) => item.fuelType === boiler_fuel_type
          );
          if (existingFuel) {
            const initialRob = parseFloat(existingFuel.Initialiy_rob || 0);
            const newRob = Math.max(0, initialRob - consumption);
            existingFuel.Initialiy_rob = newRob.toString();
          } else {
            updatedRobFuel.push({
              fuelType: boiler_fuel_type,
              Initialiy_rob: (0 - consumption).toString(),
            });
          }
        }
      });
    }

    // 3. Total duration logic
    const voyageId = staffDashboard?.voyage_details?.[0]?.id || "0";
    if (
      voyageId !== "0" &&
      values.logDate &&
      values.time &&
      previousLog?.log_date &&
      previousLog?.log_time
    ) {
      const currentDateTime = moment(
        `${values.logDate.format("YYYY-MM-DD")} ${values.time.format(
          "HH:mm:ss"
        )}`,
        "YYYY-MM-DD HH:mm:ss"
      );
      const previousDateTime = moment(
        `${previousLog.log_date} ${previousLog.log_time}`,
        "YYYY-MM-DD HH:mm:ss"
      );
      if (currentDateTime.isValid() && previousDateTime.isValid()) {
        const currentHours =
          currentDateTime.hours() + currentDateTime.minutes() / 60;
        const previousHours =
          previousDateTime.hours() + previousDateTime.minutes() / 60;
        let timeDiff;
        if (currentHours === previousHours) timeDiff = 24;
        else if (currentHours > previousHours)
          timeDiff = currentHours - previousHours;
        else timeDiff = 24 + currentHours - previousHours;
        const hrsAdvRtd = parseFloat(values.hrs_adv_rtd || 0);
        newCalculations.total_duration =
          Math.round((+hrsAdvRtd + timeDiff) * 100) / 100;
      }
    }

    // 4. Stay-specific duration
    if (values.stay && newCalculations.total_duration > 0) {
      const duration = newCalculations.total_duration;
      switch (values.stay) {
        case "SS":
          newCalculations.ss = duration;
          break;
        case "HM":
          newCalculations.hm = duration;
          break;
        case "PORT":
          newCalculations.port = duration;
          break;
        case "DRIFT":
          newCalculations.drift = duration;
          break;
        case "ANCH":
          newCalculations.anchor = duration;
          break;
        default:
          break;
      }
    }

    // 5. Speed, Engine Distance, RPM, Slip (if stay is SS)
    if (values.stay === "SS" && previousLog?.me_revolution_counter != null) {
      const meRevolutionCounter = parseFloat(values.me_revolution_counter);
      const prevMeRevolutionCounter =
        parseFloat(previousLog.me_revolution_counter) || 0;
      const obsDistance = parseFloat(values.obs_distance);
      if (
        !isNaN(meRevolutionCounter) &&
        meRevolutionCounter >= prevMeRevolutionCounter
      ) {
        newCalculations.total_revolution =
          meRevolutionCounter - prevMeRevolutionCounter;
        if (newCalculations.total_duration > 0) {
          newCalculations.rpm =
            Math.round(
              (newCalculations.total_revolution /
                (newCalculations.total_duration * 60)) *
                100
            ) / 100;
          newCalculations.engine_distance =
            Math.round(
              ((newCalculations.rpm *
                newCalculations.total_duration *
                60 *
                previousLog.pitch) /
                1852) *
                100
            ) / 100;
        }
      }

      if (!isNaN(obsDistance) && newCalculations.total_duration > 0) {
        newCalculations.speed =
          Math.round((obsDistance / newCalculations.total_duration) * 100) /
          100;
      }

      if (
        !isNaN(obsDistance) &&
        obsDistance !== 0 &&
        newCalculations.engine_distance &&
        newCalculations.engine_distance !== 0
      ) {
        newCalculations.slip =
          Math.round(
            (((newCalculations.engine_distance - obsDistance) * 100) /
              newCalculations.engine_distance) *
              100
          ) / 100;
      }
    }
    return { calculations: newCalculations, robFuel: updatedRobFuel };
  };

  // Handle input changes and update calculations
  const handleValuesChange = (changedValues, allValues) => {
    const { calculations: newCalculations, robFuel: newRobFuel } =
      calculateValues(allValues);
    setCalculations(newCalculations);
    form.setFieldsValue({
      total_duration_ss: newCalculations.ss || undefined,
      total_duration_hm: newCalculations.hm || undefined,
      total_duration_port: newCalculations.port || undefined,
      total_duration_drift: newCalculations.drift || undefined,
      total_duration_anchor: newCalculations.anchor || undefined,
      total_revolution: newCalculations.total_revolution || undefined,
      rpm: newCalculations.rpm || undefined,
      engine_distance: newCalculations.engine_distance || undefined,
      speed: newCalculations.speed || undefined,
      slip: newCalculations.slip || undefined,
      rob_other: newCalculations.rob_other || undefined,
    });

    // Update ROB fields
    newRobFuel.forEach((item) => {
      form.setFieldsValue({
        [`rob_${item.fuelType.toLowerCase()}`]: item.Initialiy_rob,
      });
    });
  };

  // Form submission
  const onFinish = async (values) => {
    setLoader(true);
    try {
      const formattedValues = {
        ...values,
        logDate: values.logDate ? values.logDate.format("YYYY-MM-DD") : "",
        time: values.time ? values.time.format("HH:mm:ss") : "",
        total_duration_ss: calculations.ss || "",
        total_duration_hm: calculations.hm || "",
        total_duration_port: calculations.port || "",
        total_duration_drift: calculations.drift || "",
        total_duration_anchor: calculations.anchor || "",
        total_revolution: calculations.total_revolution || "",
        rpm: calculations.rpm || "",
        engine_distance: calculations.engine_distance || "",
        speed: calculations.speed || "",
        slip: calculations.slip || "",
      };

      const FORM_DATA = new FormData();
      FORM_DATA.append(
        "voyage_id",
        staffDashboard?.voyage_details?.[0]?.id || "0"
      );
      FORM_DATA.append("log_date", formattedValues.logDate);
      FORM_DATA.append("log_time", formattedValues.time);
      FORM_DATA.append("place", formattedValues.place_port || "");
      FORM_DATA.append("hrs_adv_rtd", formattedValues.hrs_adv_rtd || "0");
      FORM_DATA.append(
        "me_revolution_counter",
        formattedValues.me_revolution_counter || ""
      );
      FORM_DATA.append("obs_distance", formattedValues.obs_distance || "");
      FORM_DATA.append(
        "aux_engine_consumption_cargo",
        formattedValues.aux_engine_fuel_consumption_cargo || ""
      );
      FORM_DATA.append(
        "boiler_fuel_consumption_cargo",
        formattedValues.boiler_fuel_consumption_cargo || ""
      );
      FORM_DATA.append(
        "fresh_water_consumption",
        formattedValues.fresh_water_consumption || ""
      );
      FORM_DATA.append("fw_production", formattedValues.fw_production || "");
      FORM_DATA.append("quantity", formattedValues.bunker_quantity || "");
      FORM_DATA.append(
        "total_me_fuel_consumption",
        formattedValues.main_engine_fuel_consumption || ""
      );
      FORM_DATA.append(
        "total_me_fuel_type",
        formattedValues.main_fuel_oil_type || ""
      );
      FORM_DATA.append(
        "total_aux_engine_fuel_consumption",
        formattedValues.auxiliary_engine_fuel_consumption || ""
      );
      FORM_DATA.append(
        "total_aux_engine_fuel_type",
        formattedValues.auxi_fuel_oil_type || ""
      );
      FORM_DATA.append(
        "total_boiler_fuel_consumption",
        formattedValues.boiler_fuel_consumption || ""
      );
      FORM_DATA.append(
        "total_boiler_fuel_type",
        formattedValues.boiler_fuel_oil_type || ""
      );
      FORM_DATA.append(
        "total_cargo_machinery_fuel_consumption",
        formattedValues.cargo_fuel_consumption || ""
      );
      FORM_DATA.append(
        "total_cargo_machinery_fuel_type",
        formattedValues.cargo_fuel_oil_type || ""
      );
      FORM_DATA.append(
        "lub_oil_consumption",
        formattedValues.cylinder_oil_consumption || ""
      );
      FORM_DATA.append("lub_oil_type", formattedValues.lub_oil_type || "");
      FORM_DATA.append(
        "bunker_received",
        formattedValues.bunker_received ? "true" : "false"
      );
      FORM_DATA.append("bunker_grade_type", formattedValues.bunker_grade || "");
      FORM_DATA.append("type_of_event", formattedValues.type_of_event || "");
      FORM_DATA.append("stay", formattedValues.stay || "");
      FORM_DATA.append(
        "total_duration_ss",
        formattedValues.total_duration_ss || ""
      );
      FORM_DATA.append(
        "total_duration_hm",
        formattedValues.total_duration_hm || ""
      );
      FORM_DATA.append(
        "total_duration_port",
        formattedValues.total_duration_port || ""
      );
      FORM_DATA.append(
        "total_duration_drift",
        formattedValues.total_duration_drift || ""
      );
      FORM_DATA.append(
        "total_duration_anchor",
        formattedValues.total_duration_anchor || ""
      );
      FORM_DATA.append(
        "total_revolution",
        formattedValues.total_revolution || ""
      );
      FORM_DATA.append("rpm", formattedValues.rpm || "");
      FORM_DATA.append(
        "engine_distance",
        formattedValues.engine_distance || ""
      );
      FORM_DATA.append("speed", formattedValues.speed || "");
      FORM_DATA.append("slip", formattedValues.slip || "");
      FORM_DATA.append("rob_other", formattedValues.rob_other || "");

      robFuel.forEach((item) => {
        FORM_DATA.append(
          `rob_${item.fuelType.toLowerCase()}`,
          item.Initialiy_rob || ""
        );
      });

      if (Array.isArray(values.me_other_consumption)) {
        values.me_other_consumption.forEach((item) => {
          FORM_DATA.append("fuel_type", item.fuel_type || "");
          FORM_DATA.append("fuel_consumption", item.fuel_consumption || "");
        });
      }

      // Lub Oil Other consumptions - FORM_DATA append
      if (Array.isArray(values.lub_other_consumption)) {
        values.lub_other_consumption.forEach((item) => {
          if (!item) return;

          switch (item.lub_fuel_type) {
            case "LTBN":
              FORM_DATA.append(
                "ltbn_fuel_type_other",
                item.lub_fuel_type || ""
              );
              FORM_DATA.append(
                "ltbn_fuel_consumption_other",
                item.lub_fuel_consumption || ""
              );
              break;

            case "AECC":
              FORM_DATA.append(
                "aecc_fuel_type_other",
                item.lub_fuel_type || ""
              );
              FORM_DATA.append(
                "aecc_fuel_consumption_other",
                item.lub_fuel_consumption || ""
              );
              break;

            case "MECC":
              FORM_DATA.append(
                "mecc_fuel_type_other",
                item.lub_fuel_type || ""
              );
              FORM_DATA.append(
                "mecc_fuel_consumption_other",
                item.lub_fuel_consumption || ""
              );
              break;

            default:
              // agar "other" types ko skip karna hai to kuch mat bhejo
              break;
          }
        });
      }

      if (Array.isArray(values.auxi_other_consumption)) {
        values.auxi_other_consumption.forEach((item) => {
          FORM_DATA.append("auxi_fuel_type_other", item.auxi_fuel_type || "");
          FORM_DATA.append(
            "auxi_fuel_consumption_other",
            item.auxi_fuel_consumption || ""
          );
        });
      }

      if (Array.isArray(values.boiler_other_consumption)) {
        values.boiler_other_consumption.forEach((item) => {
          FORM_DATA.append(
            "boiler_fuel_type_other",
            item.boiler_fuel_type || ""
          );
          FORM_DATA.append(
            "boiler_fuel_consumption_other",
            item.boiler_fuel_consumption || ""
          );
        });
      }

      const res = await post(ApiURL.create_daily_consumption_log, FORM_DATA);

      if (res?.status === true) {
        SuccessToast(
          "Success Message",
          res?.message || "Daily Consumption Log Added Successfully"
        );
        form.resetFields();
        setCalculations({
          total_duration: 0,
          anchor: 0,
          ss: 0,
          drift: 0,
          hm: 0,
          port: 0,
          total_revolution: null,
          rpm: null,
          engine_distance: null,
          speed: null,
          slip: null,
        });
        if (staffDashboard?.voyage_details?.[0]?.id) {
          fetchPreviousLog(staffDashboard.voyage_details[0].id);
        }
      } else {
        ErrorToast("Error", res?.error || "Failed to save log.");
      }
    } catch (error) {
      ErrorToast("Error", "An unexpected error occurred.");
    } finally {
      setLoader(false);
    }
  };

  // Render duration input based on stay
  const renderDurationInput = (stay) => {
    const durationFields = {
      SS: { name: "total_duration_ss", label: "SS (hrs)" },
      HM: { name: "total_duration_hm", label: "HM (hrs)" },
      PORT: { name: "total_duration_port", label: "Port (hrs)" },
      DRIFT: { name: "total_duration_drift", label: "Drift (hrs)" },
      ANCH: { name: "total_duration_anchor", label: "Anchor (hrs)" },
    };

    const field = durationFields[stay];
    if (!field) return null;

    return (
      <Form.Item name={field.name} label={`Total Duration (${field.label})`}>
        <Input placeholder={field.label} disabled />
      </Form.Item>
    );
  };

  const handleExternalMe = () => {
    const otherConsumptionMe = form.getFieldValue("me_other_consumption") || [];
    if (otherConsumptionMe.length === 0 && addOtherConsumptionRef.current) {
      addOtherConsumptionRef.current();
    } else {
      WarningToast(
        "Warning message",
        "You can only add one fuel consumption entry."
      );
    }
  };

  const handleExternalAddLubOil = () => {
    const otherConsumptionLub =
      form.getFieldValue("lub_other_consumption") || [];
    if (otherConsumptionLub.length < 3) {
      if (addOtherlubeConsumptionRef.current) {
        addOtherlubeConsumptionRef.current();
      }
    } else {
      WarningToast(
        "Limit Reached",
        "You can only add up to 3 fuel consumption entries."
      );
    }
  };

  const handleExternalAuxi = () => {
    const otherConsumptionAuxi =
      form.getFieldValue("auxi_other_consumption") || [];
    if (
      otherConsumptionAuxi.length === 0 &&
      addOtherAuxiConsumptionRef.current
    ) {
      if (addOtherAuxiConsumptionRef.current) {
        addOtherAuxiConsumptionRef.current();
      }
    } else {
      WarningToast(
        "Warning message",
        "You can only add one fuel consumption entry."
      );
    }
  };

  const handleExternalBoiler = () => {
    const otherConsumptionBoiler =
      form.getFieldValue("boiler_other_consumption") || [];
    if (
      otherConsumptionBoiler.length === 0 &&
      addOtherBoilerConsumptionRef.current
    ) {
      if (addOtherBoilerConsumptionRef.current) {
        addOtherBoilerConsumptionRef.current();
      }
    } else {
      WarningToast(
        "Warning message",
        "You can only add one fuel consumption entry."
      );
    }
  };

  return (
    <div className="white-container">
      <div
        className="theme-content-left-head"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0 }}>Daily Consumption</h3>
      </div>

      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={handleValuesChange}
        layout="vertical"
        initialValues={{
          logDate: null,
          bunker_received: false,
          time: undefined,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="logDate" label="Date">
              <DatePicker
                format="YYYY-MM-DD"
                placeholder="Select Date"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="place_port"
              label="Place/Port (at sea or channel name)"
            >
              <Input placeholder="Place/Port" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="type_of_event" label="Type of Event">
              <Select
                placeholder="-- Select Type of Event --"
                options={typeOfEventOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="time" label="Time (HH:mm:ss)">
              <TimePicker
                format="HH:mm:ss"
                placeholder="Select Time"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="voyage_number" label="Voyage Number">
              <Input placeholder="Voyage Number" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="hrs_adv_rtd"
              label={
                <>
                  Hrs ADV/RTD (+1 or -1)
                  <Tooltip
                    title="For Advance, enter -1. For Retard, enter +1."
                    placement="right"
                  >
                    <InfoCircleOutlined
                      style={{
                        marginLeft: 8,
                        color: "red",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </>
              }
            >
              <Input placeholder="Hours" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="stay" label="Stay">
              <Select placeholder="-- Select Stay --" options={stayData} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.stay !== curr.stay}
            >
              {({ getFieldValue }) =>
                renderDurationInput(getFieldValue("stay"))
              }
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="me_revolution_counter"
              label="M/E Revolution Counter"
            >
              <Input placeholder="Count" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="total_revolution" label="Total Revolution">
              <Input
                placeholder="Count"
                disabled
                value={calculations.total_revolution}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="rpm" label="RPM">
              <Input placeholder="RPM" disabled value={calculations.rpm} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="obs_distance"
              label="Obs. Distance (nautical miles)"
            >
              <Input placeholder="Distance in NM" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="engine_distance"
              label="Engine Distance (nautical miles)"
            >
              <Input
                placeholder="Distance in NM"
                disabled
                value={calculations.engine_distance}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="speed" label="Speed (knots)">
              <Input
                placeholder="Speed in knots"
                disabled
                value={calculations.speed}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="slip" label="Slip (%)">
              <Input
                placeholder="Slip in %"
                disabled
                value={calculations.slip}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="main_engine_fuel_consumption"
              label="Total M/E Fuel Consumption (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="main_fuel_oil_type" label="Fuel Type">
              <Select
                placeholder="-- Select Main Fuel Type --"
                options={fuelTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col
            span={3}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <label style={{ marginBottom: 8, marginTop: 10, fontSize: 15 }}>
              Other Fuel
            </label>
            <PlusCircleOutlined
              onClick={handleExternalMe}
              style={{ fontSize: 16, color: "red", cursor: "pointer" }}
            />
          </Col>
        </Row>

        <Form.List name="me_other_consumption">
          {(fields, { add, remove }) => {
            addOtherConsumptionRef.current = add;
            return (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={16} key={key} align="middle">
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[name, "fuel_consumption"]}
                        label="Other M/E Fuel Consumption (tons)"
                      >
                        <Input placeholder="Consumption in tons" />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        {...restField}
                        name={[name, "fuel_type"]}
                        label="Fuel Type"
                      >
                        <Select
                          placeholder="Select Fuel Type"
                          options={fuelTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ marginTop: 10, fontSize: 16, color: "red" }}
                      />
                    </Col>
                  </Row>
                ))}
              </>
            );
          }}
        </Form.List>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="auxiliary_engine_fuel_consumption"
              label="Total Aux. Engine Fuel Consumption (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="auxi_fuel_oil_type" label="Fuel Type">
              <Select
                placeholder="-- Select Auxi Fuel Type --"
                options={fuelTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col
            span={3}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <label style={{ marginBottom: 8, marginTop: 10, fontSize: 15 }}>
              Other Fuel
            </label>
            <PlusCircleOutlined
              onClick={handleExternalAuxi}
              style={{ fontSize: 16, color: "red", cursor: "pointer" }}
            />
          </Col>
        </Row>

        <Form.List name="auxi_other_consumption">
          {(fields, { add, remove }) => {
            addOtherAuxiConsumptionRef.current = add;
            return (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={16} key={key} align="middle">
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[name, "auxi_fuel_consumption"]}
                        label="Other Auxiliary Fuel Consumption (tons)"
                      >
                        <Input placeholder="Consumption in tons" />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        {...restField}
                        name={[name, "auxi_fuel_type"]}
                        label="Fuel Type"
                      >
                        <Select
                          placeholder="Select Fuel Type"
                          options={fuelTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ marginTop: 10, fontSize: 16, color: "red" }}
                      />
                    </Col>
                  </Row>
                ))}
              </>
            );
          }}
        </Form.List>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="aux_engine_fuel_consumption_cargo"
              label="Aux. Engine Consumption for Cargo (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="boiler_fuel_consumption"
              label="Total Boiler Fuel Consumption (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="boiler_fuel_oil_type" label="Fuel Type">
              <Select
                placeholder="-- Select Boiler Fuel Type --"
                options={fuelTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col
            span={3}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <label style={{ marginBottom: 8, marginTop: 10, fontSize: 15 }}>
              Other Fuel
            </label>
            <PlusCircleOutlined
              onClick={handleExternalBoiler}
              style={{ fontSize: 16, color: "red", cursor: "pointer" }}
            />
          </Col>
        </Row>

        <Form.List name="boiler_other_consumption">
          {(fields, { add, remove }) => {
            addOtherBoilerConsumptionRef.current = add;
            return (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={16} key={key} align="middle">
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[name, "boiler_fuel_consumption"]}
                        label="Other Boiler Fuel Consumption (tons)"
                      >
                        <Input placeholder="Consumption in tons" />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        {...restField}
                        name={[name, "boiler_fuel_type"]}
                        label="Fuel Type"
                      >
                        <Select
                          placeholder="Select Fuel Type"
                          options={fuelTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ marginTop: 10, fontSize: 16, color: "red" }}
                      />
                    </Col>
                  </Row>
                ))}
              </>
            );
          }}
        </Form.List>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="boiler_fuel_consumption_cargo"
              label="Boiler Fuel Oil Consumption for Cargo (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="cargo_fuel_consumption"
              label="Total Cargo Machinery Fuel Consumption (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="cargo_fuel_oil_type" label="Fuel Type">
              <Select
                placeholder="-- Select Cargo Fuel Type --"
                options={fuelTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="cylinder_oil_consumption"
              label="Lub Oil Consumption (litres)"
            >
              <Input placeholder="Consumption in litres" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="lub_oil_type" label="Lub Oil Type">
              <Select
                placeholder="-- Select Lub Oil Type --"
                options={lubOilTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col
            span={3}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <label style={{ marginBottom: 8, marginTop: 10, fontSize: 15 }}>
              Other Fuel
            </label>
            <PlusCircleOutlined
              onClick={handleExternalAddLubOil}
              style={{ fontSize: 16, color: "red", cursor: "pointer" }}
            />
          </Col>
        </Row>

        <Form.List name="lub_other_consumption">
          {(fields, { add, remove }) => {
            addOtherlubeConsumptionRef.current = add;
            return (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={16} key={key} align="middle">
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[name, "lub_fuel_consumption"]}
                        label="Other Lub Oil Consumption (litres)"
                      >
                        <Input placeholder="Consumption in (litres)" />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        {...restField}
                        name={[name, "lub_fuel_type"]}
                        label="Lub Oil Type"
                      >
                        <Select
                          placeholder="-- Select Lub Oil Type --"
                          options={lubOilTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ marginTop: 10, fontSize: 16, color: "red" }}
                      />
                    </Col>
                  </Row>
                ))}
              </>
            );
          }}
        </Form.List>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="fresh_water_consumption"
              label="Fresh Water Consumption (tons)"
            >
              <Input placeholder="Consumption in tons" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="fw_production"
              label="Fresh Water Production (tons)"
            >
              <Input placeholder="Production in tons" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="bunker_received" label="Bunker Received (Yes/No)">
              <Select
                placeholder="-- Select Yes/No --"
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.bunker_received !== curr.bunker_received
              }
            >
              {({ getFieldValue }) => (
                <Form.Item name="bunker_grade" label="Bunker Grade">
                  <Select
                    placeholder="-- Select Bunker Grade --"
                    options={bunkerGradeOptions}
                    disabled={!getFieldValue("bunker_received")}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.bunker_received !== curr.bunker_received
              }
            >
              {({ getFieldValue }) => (
                <Form.Item
                  name="bunker_quantity"
                  label="Quantity (tons/litres)"
                >
                  <Input
                    placeholder="Quantity"
                    disabled={!getFieldValue("bunker_received")}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {robFuel.map((item, index) => (
            <Col span={12} key={index}>
              <Form.Item
                label={`ROB ${item.fuelType} (tons)`}
                name={`rob_${item.fuelType.toLowerCase()}`}
                initialValue={item.Initialiy_rob}
              >
                <Input placeholder={`ROB ${item.fuelType}`} disabled />
              </Form.Item>
            </Col>
          ))}

          {/* Always render ROB OTHER at the end */}
          <Col span={12}>
            <Form.Item name="rob_other" label="ROB OTHER (MT)">
              <Input placeholder="ROB Other" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="save_btn"
                style={{ color: "white" }}
                loading={loader}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default DailyConsumption;
